import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Season } from "../../../types/season";
import { getSeason, getSeasonTasks, postAcquireTaskReward } from "../../../api";
import DoingIcon from "../icons/doing";
import DoneIcon from "../icons/done";
import UndoIcon from "../icons/undo";
import CheckingIcon from "../icons/checking";
import RedPockerMiniIcon from "../icons/red-packet-mini";
import styles from "./index.module.scss";
import cn from "classnames";
import { getTaskTypeName } from "../../../utils/season";
import dayjs from "dayjs";
import DoneRedIcon from "../icons/done-red";
import { Loading, Modal, Notification } from "@quark/design";
import Navigator from "../../../component/navigator";
import {
  getDay,
  getDayOffset,
  getMonthAndDay,
  getOffsetText,
} from "../../../utils/date";
import classes from "classnames";
import useProfile from "../../../hooks/use-profile";
import useDocumentTitle from "../../../hooks/use-document-title";
import SeasonHistory from "../history";

const SeasonTaskPage = () => {
  const navigate = useNavigate();
  const params = useParams();
  const profile = useProfile();
  const { seasonId = 1 } = params;
  const [season, setSeason] = useState<Season.Season>();
  const [tasks, setTasks] = useState<Season.Task[]>([]);
  const [loading, setLoading] = useState(false);
  const curDay = useMemo(
    () => dayjs(profile?.server_time).format("YYYY-MM-DD 00:00:00"),
    [profile]
  );
  const curTask = useMemo(() => {
    return tasks?.find((item) => item.day === curDay);
  }, [tasks, curDay]);
  const [acquiredTaskId, setAcquiredTaskId] = useState(0);
  const [acquireSuccessVisible, setAcquireSuccessVisible] = useState(false);
  useDocumentTitle("打卡任务");
  const sharedTasks = useMemo(() => {
    return tasks
      .map((t) => {
        return t.sub_tasks.find((st) => st.task_type === "share_punch");
      })
      .filter((t) => t?.attachment)
      .reverse();
  }, [tasks]);

  console.log(sharedTasks);

  const fakeTasks = useMemo(() => {
    const fakeTasks: ReactNode[] = [];
    const firstTask = tasks[0];
    if (!firstTask) return fakeTasks;
    const offsets = getDayOffset(firstTask.day);
    for (let i = 0; i < offsets; i++) {
      fakeTasks.push(<div key={i} className={cn(styles.task, "fake")} />);
    }
    return fakeTasks;
  }, [tasks]);

  const isLearnDone = useMemo(() => {
    const learnTask = curTask?.sub_tasks.find(
      (item) => item.task_type === "learn_page"
    );
    return learnTask?.status === "done";
  }, [curTask]);

  const getTasks = useCallback(() => {
    const id = Number(seasonId);
    setLoading(true);
    Promise.all([getSeason(id), getSeasonTasks(id)])
      .then(([seasonData, tasksData]) => {
        setSeason(seasonData);
        setTasks(tasksData);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [seasonId]);

  const action = (task_type: Season.TaskType, sub_task_id: number) => {
    if (task_type === "learn_page") {
      navigate("/course/list");
      return;
    }
    if (task_type === "share_punch") {
      navigate(`/season/${seasonId}/task/${curTask?.id}/punch/${sub_task_id}`);
      return;
    }
    Notification.error("未定义行为", { duration: 3000 });
  };

  const getReward = (taskId: number) => {
    if (acquiredTaskId) return;
    setAcquiredTaskId(taskId);
    postAcquireTaskReward(taskId)
      .then(() => {
        setAcquireSuccessVisible(true);
        getTasks();
      })
      .finally(() => {
        setAcquiredTaskId(0);
      });
  };

  const gotoWallet = () => {
    navigate("/promotion");
  };

  useEffect(() => {
    getTasks();
  }, [seasonId, getTasks]);

  if (loading) return <Loading />;

  return (
    <div className={styles.container}>
      <Navigator />
      <div className={styles.content}>
        <div className={styles.main}>
          <div className={styles.schedule}>
            {!!season && (
              <div className={styles.season}>
                <div className={styles.line}>
                  <div className={styles.label}>活动名称:</div>
                  {season?.name}
                  <a
                    href={season.explain_url}
                    target="__blank"
                    style={{ marginLeft: 70 }}
                  >
                    活动说明
                  </a>
                </div>
                <div className={styles.line}>
                  <div className={styles.label}>活动时间:</div>从
                  <div className={styles.date}>
                    {getMonthAndDay(season.start_day || "")}
                  </div>
                  到
                  <div className={styles.date}>
                    {getMonthAndDay(season.end_day || "")}
                  </div>
                  ,&nbsp;
                  {getOffsetText(season.start_day, season.end_day)}
                </div>
              </div>
            )}
            <div className={styles.statusList}>
              <div className={styles.statusItem}>
                <DoneIcon />
                已完成
              </div>
              <div className={styles.statusItem}>
                <DoingIcon />
                进行中
              </div>
              <div className={styles.statusItem}>
                <CheckingIcon />
                审核中
              </div>
              <div className={styles.statusItem}>
                <UndoIcon />
                未完成
              </div>
            </div>

            <div className={styles.week}>
              <div className={styles.weekDay}>一</div>
              <div className={styles.weekDay}>二</div>
              <div className={styles.weekDay}>三</div>
              <div className={styles.weekDay}>四</div>
              <div className={styles.weekDay}>五</div>
              <div className={styles.weekDay}>六</div>
              <div className={styles.weekDay}>日</div>
            </div>
            <div className={styles.tasks}>
              {fakeTasks}
              {tasks?.map(({ id, day, status, scholarship }) => {
                const isCurDay = curDay === day;
                const hasScholarship = !!scholarship;
                return (
                  <div
                    className={cn(styles.task, isCurDay && styles.curTask)}
                    key={id}
                  >
                    {hasScholarship && (
                      <div className={styles.scholarship}>
                        <RedPockerMiniIcon />
                      </div>
                    )}
                    <div>{getDay(day)}</div>
                    <div className={styles.status}>
                      {status === "doing" && <DoingIcon />}
                      {status === "done" && <DoneIcon />}
                      {status === "check" && <CheckingIcon />}
                      {status === "undo" && <UndoIcon />}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className={styles.curSubTasks}>
              {curTask?.sub_tasks?.map(({ id, task_type, status }) => {
                const isDone = status === "done";
                const isCheck = status === "check";
                const taskTypeName = getTaskTypeName(task_type);
                let displayText = isDone
                  ? `已${taskTypeName}`
                  : `去${taskTypeName}`;
                if (isCheck) displayText = taskTypeName === '打卡' ? `${taskTypeName}审核中(重传)` : `${taskTypeName}审核中`;
                // disabled: 已完成或者不是学习任务并且学习任务未完成
                const disabled =
                  isDone || (task_type !== "learn_page" && !isLearnDone);
                return (
                  <div
                    className={cn(
                      styles.subTask,
                      disabled && styles.subTaskDone
                    )}
                    key={id}
                    onClick={() => {
                      if (disabled) return;
                      action(task_type, id);
                    }}
                  >
                    {displayText}
                  </div>
                );
              })}
            </div>

            <div className={styles.tips}>
              完成学习需至少学习一节课程+完成一道课后习题
            </div>
          </div>
          <div className={styles.scholarshipContainer}>
            <div className={styles.title}>奖学金进度</div>
            <div className={styles.scholarships}>
              {tasks
                ?.reduce(
                  (nodes, { scholarship }, index) => {
                    const hasScholarship = !!scholarship;
                    if (hasScholarship) {
                      const rank =
                        nodes.filter((node) => node?.name === "div").length + 1;
                      const { status } = scholarship;
                      nodes.push({
                        name: "div",
                        node: (
                          <div
                            className={cn(
                              styles.scholarship,
                              styles[`scholarship-${rank}`]
                            )}
                            key={index}
                          >
                            <div className={styles.redPocket} />
                            <div className={styles.amount}>
                              {scholarship.mount / 100}元
                            </div>
                            <div className={styles.status}>
                              {status === "not_acquired" && <UndoIcon />}
                              {status === "can_acquired" && <DoingIcon />}
                              {status === "has_acquired" && <DoneRedIcon />}
                            </div>
                          </div>
                        ),
                      });
                    } else {
                      nodes.push({
                        name: "span",
                        node: <span key={index} />,
                      });
                    }
                    return nodes;
                  },
                  [] as {
                    name: string;
                    node: React.ReactNode;
                  }[]
                )
                .map((n) => n.node)}
            </div>

            <div className={styles.scholarshipRecords}>
              {tasks?.map(({ id, scholarship }, index) => {
                if (!scholarship) return <div key={id} />;
                const isDone = scholarship.status === "has_acquired";
                return (
                  <div
                    className={cn(styles.record, isDone && styles.done)}
                    key={id}
                  >
                    <div className={styles.left}>
                      <div className={styles.recordTitle}>
                        #打卡第{index + 1}天
                      </div>
                      <div className={styles.recordSubTitle}>
                        奖励
                        <span className={styles.amount}>
                          ¥{scholarship.mount / 100}
                        </span>
                        元
                      </div>
                    </div>
                    <div
                      className={cn(
                        styles.claimButton,
                        isDone && styles.disabled,
                        scholarship.status === "not_acquired" && styles.disabled
                      )}
                      onClick={() => {
                        if (scholarship.status !== "can_acquired") return;
                        getReward(id);
                      }}
                    >
                      {acquiredTaskId === id ? (
                        <Loading />
                      ) : isDone ? (
                        "已领"
                      ) : (
                        "领取"
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className={styles.walletLink} onClick={gotoWallet}>
              立刻前往钱包提现
            </div>

            <Modal
              title="领取成功"
              visible={acquireSuccessVisible}
              onClose={() => setAcquireSuccessVisible(false)}
            >
              <div className={styles.walletLink} onClick={gotoWallet}>
                立刻前往钱包提现
              </div>
            </Modal>
          </div>
        </div>
        <div className={styles.tasks}>
          <div className={styles.title}>打卡成就墙</div>
          <div className={styles.records}>
            {sharedTasks.map((st, index) => {
              return (
                <div className={styles.record} key={index}>
                  <div className={styles.header}>
                    <div className={styles.date}>
                      {dayjs(st?.day).format("YYYY.MM.DD")}
                    </div>
                    <div
                      className={classes(
                        styles.status,
                        st?.status === "done"
                          ? styles.completed
                          : styles.pending
                      )}
                    >
                      {st?.status === "done" ? "已完成" : "审核中"}
                    </div>
                  </div>
                  <div className={styles.cover}>
                    <img src={st?.attachment} alt="" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <SeasonHistory />
    </div>
  );
};

export default SeasonTaskPage;
